import type React from 'react';
import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useHover } from 'react-use';
import classNames from 'classnames';
import { useStore } from 'effector-react';
import { KRP_TARGET_ID } from '@constants';
import useToggle from '@hooks/useToggle';
import { $editingHotspot } from '@stores/editingHotspotStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import IconPicker from '@components/icons/IconPicker';

interface Props {
  className?: string;
  onChange: (colorString: string) => void;
}

function KrpColorPicker({ className, onChange }: Props) {
  const { hotspot, isBatch, batchHotspots } = useStore($editingHotspot)!;

  const pickMode = useToggle(false);
  const posRef = useRef({ top: 0, left: 0, width: 0, height: 0 });
  const origChromakeyRef = useRef('');

  const handleClick = () => {
    origChromakeyRef.current =
      MainPlayer.get()?.getHotspotById(
        isBatch ? batchHotspots![0].id : hotspot.id
      ).chromakey || '';
    if (isBatch) {
      batchHotspots?.forEach((hs) => {
        MainPlayer.get()?.updateHotspot({ id: hs.id, chromakey: '' });
      });
    } else {
      MainPlayer.get()?.updateHotspot({ id: hotspot.id, chromakey: '' });
    }
    const krpObj = document.getElementById(KRP_TARGET_ID);
    if (!krpObj) return;
    const rect = krpObj.getBoundingClientRect();
    posRef.current = {
      top: rect.top,
      width: rect.width,
      height: rect.height,
      left: rect.left,
    };
    pickMode.on();
  };

  const handleOutterClick = () => {
    if (isBatch) {
      batchHotspots?.forEach((hs) => {
        MainPlayer.get()?.updateHotspot({
          id: hs.id,
          chromakey: origChromakeyRef.current,
        });
      });
    } else {
      MainPlayer.get()?.updateHotspot({
        id: hotspot.id,
        chromakey: origChromakeyRef.current,
      });
    }
    pickMode.off();
  };

  const handleInnerClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const canvas = MainPlayer.get()?.screenshotCanvas();
    if (!canvas) {
      handleOutterClick();
      return;
    }
    const node = e.target as HTMLElement;
    const res = canvas
      .getContext('2d')
      ?.getImageData(
        e.pageX - node.offsetLeft,
        e.pageY - node.offsetTop,
        1,
        1
      ).data;
    if (!res) {
      handleOutterClick();
      return;
    }
    onChange(`${res[0]},${res[1]},${res[2]},1`);
    pickMode.off();
  };

  const [btn] = useHover((isHovered) => (
    <div
      className={classNames(
        'flex h-30 w-30 cursor-pointer items-center justify-center rounded bg-black-#34',
        className
      )}
      onClick={handleClick}
    >
      <IconPicker active={isHovered || pickMode.isOn} />
    </div>
  ));

  return (
    <>
      {btn}
      {pickMode.isOn &&
        createPortal(
          <div className="fixed inset-0" onClick={handleOutterClick}>
            <div
              className="absolute cursor-crosshair"
              style={{
                ...posRef.current,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
              }}
              onClick={handleInnerClick}
            />
          </div>,
          document.body
        )}
    </>
  );
}

export default KrpColorPicker;
